import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { BlogTag } from '@/components/elements/Blog/BlogTag'
import { Interviewee } from '@/components/elements/Blog/Interviewee'
import { HTMLParser } from '@/components/elements/HTMLParser'
import { Spacer } from '@/components/elements/Spacer'

import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { Blog } from '@/types/blog'

export const BlogArticle = ({
  title,
  coverImage,
  tag,
  interviewee,
  body,
  publishDate,
}: Blog) => {
  const isMobile = useMediaQuery('(max-width: 1047px)')
  const cover = isMobile ? coverImage.spCoverImage : coverImage.pcCoverImage

  return (
    <Article>
      <header>
        <CoverImage alt={cover.altText} src={cover.src} />
        <Spacer h={{ sp: 40, pc: 30 }} />
        <MetaInfo>
          <Date dateTime={publishDate}>
            {formatDate(publishDate, 'yyyy.MM.dd')}
          </Date>
          <Spacer h={{ sp: 12, pc: 22 }} />
          <Tag>
            {tag.map((item) => (
              <BlogTag {...item} key={item._id} />
            ))}
          </Tag>
          <Spacer h={{ sp: 22, pc: 28 }} />
          <Title>{title}</Title>
          <Spacer h={{ sp: 26, pc: 40 }} />
          <Interviewee {...interviewee} />
          <Spacer h={{ sp: 46, pc: 64 }} />
        </MetaInfo>
      </header>
      <Section>
        <HTMLParser text={body} />
      </Section>
    </Article>
  )
}

const Article = styled.article`
  width: 1040px;
  margin: 74px auto 130px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 39px 0 80px;
  }
`

const CoverImage = styled.img`
  width: 1040px;
  height: 360px;
  border-radius: 12px;
  object-fit: cover;

  @media screen and (max-width: 1047px) {
    width: 100%;
    height: 135px;
    border-radius: 0;
  }
`

const Date = styled.time`
  font-size: 16px;
  letter-spacing: 1.6px;
  line-height: 25px;
  color: ${theme.colors.textSecond};

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    letter-spacing: 1.4px;
  }
`

const MetaInfo = styled.div`
  @media screen and (max-width: 1047px) {
    padding: 0 20px;
  }
`

const Tag = styled.div`
  display: flex;
  gap: 10px;
`

const Title = styled.h1`
  font-size: 28px;
  letter-spacing: 2.8px;
  line-height: 40px;
  font-weight: bold;
  color: ${theme.colors.textTitle};

  @media screen and (max-width: 1047px) {
    font-size: 22px;
    letter-spacing: 2.2px;
    line-height: 28px;
  }
`

const Section = styled.section`
  position: relative;

  p {
    font-size: 18px;
    letter-spacing: 0.36px;
    line-height: 40px;
    color: ${theme.colors.textTitle};
    margin-bottom: 20px;
  }

  a {
    color: ${theme.colors.hover};

    &:hover {
      text-decoration: underline;
    }

    > svg {
      margin-left: 8px;
      fill: ${theme.colors.hover};
    }
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  img {
    max-width: 100%;
  }

  h2 {
    font-size: 24px;
    font-weight: bold;
    letter-spacing: 1.44px;
    line-height: 36px;
    color: ${theme.colors.hover};
    margin-top: 40px;
    margin-bottom: 20px;
    margin-left: 12px;

    &:before {
      content: '';
      height: 29px;
      width: 4px;
      background-color: ${theme.colors.hover};
      position: absolute;
      left: 0;
      margin-top: 6px;
    }
  }

  h3 {
    font-size: 22px;
    font-weight: bold;
    letter-spacing: 1.32px;
    line-height: 36px;
    color: ${theme.colors.textTitle};
    margin-top: 40px;
    margin-bottom: 20px;
    margin-left: 12px;

    &:before {
      content: '';
      height: 29px;
      width: 4px;
      background-color: ${theme.colors.hover};
      position: absolute;
      left: 0;
      margin-top: 5px;
    }
  }

  @media screen and (max-width: 1047px) {
    padding: 0 20px;

    p {
      font-size: 16px;
      letter-spacing: 0.32px;
      line-height: 30px;
    }

    a {
      text-decoration: underline;
    }

    h2 {
      font-size: 22px;
      letter-spacing: 0.44px;
      line-height: 28px;
      margin-top: 19px;
      margin-bottom: 18px;
      margin-left: 15px;

      &:before {
        height: 32px;
        margin: 0;
        left: 20px;
      }
    }

    h3 {
      font-size: 20px;
      letter-spacing: 0.4px;
      line-height: 28px;
      margin-top: 18px;
      margin-bottom: 18px;
      margin-left: 15px;

      &:before {
        height: 32px;
        margin-top: -3px;
        left: 20px;
      }
    }
  }
`
